import { LocalNotifications } from '@capacitor/local-notifications';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import * as Sentry from '@sentry/capacitor';
import axios from 'axios';
import { RxCollection, RxDocument } from 'rxdb';
import {
	combineLatest,
	concatMap,
	defer,
	distinctUntilChanged,
	Subject,
	timer,
} from 'rxjs';
import { first } from 'rxjs/operators';

import {
	Booking,
	CheckInPassengerView,
	DataChange,
	NormalizedScheduledLeg,
	PushNotificationTypeEnum,
	SchedulerStateEnum,
	SchedulerView,
	TDriversScheduleResponse,
} from '@mopla/data-models';
import { Logger } from '@mopla/logger';
import {
	isBookingStatusCancelled,
	poll,
	safeBulkUpsert,
	safeRemoveDocument,
} from '@mopla/utils';

import {
	EditBookingAction,
	EditLegAction,
	failCheckInAction,
	InitEventStreamAction,
	InitPushProcessorAction,
	loadSchedule as loadScheduleAction,
	LoadScheduleAction,
	ResetScheduleAction,
	ScheduleActionTypes,
	SetBookingsAction,
	SetLegsAction,
	startCheckInAction,
	stopCheckInAction,
	updateSchedule,
	UpdateScheduleAction,
} from '../actions/scheduleActions';
import { Action, Dependencies, Effect } from '../business-logic';
import { ScheduleDiffEl } from '../entities/scheduleDiff';
import { useScheduleChangesStore } from '../hooks/useScheduleChangesStore';
import { prepareNormalizedScheduledLegs } from '../mappers/prepareNormalizedScheduledLegs';
import { ofType } from '../operators/ofType';
import { networkStatus$ } from '../subjects/network';
import { areBookingsEqual } from '../utils/areDriverBookingsEqual';
import { buildScheduleDiff } from '../utils/buildScheduleDiff';

let pushInitialized = false;

/** Not used */
export const setLegsEffect: Effect<SetLegsAction> = (actions$, dependencies) =>
	actions$.pipe(
		ofType(ScheduleActionTypes.SetLegs),
		concatMap((action: SetLegsAction) =>
			defer(async () => {
				await dependencies.db['scheduledLeg'].bulkUpsert(
					Object.values(action.payload)
				);
			})
		)
	);

export const editLegEffect: Effect<EditLegAction> = (actions$, dependencies) =>
	actions$.pipe(
		ofType(ScheduleActionTypes.EditLeg),
		concatMap((action: EditLegAction) =>
			defer(async () => {
				const lastChange = await dependencies.db['dataChanges']
					.findOne({ selector: {}, sort: [{ changeId: 'desc' }] })
					.exec();
				const lastChangeObj = lastChange?.toJSON();

				await dependencies.db['dataChanges'].upsert({
					changeId: lastChangeObj ? `${+lastChangeObj.changeId + 1}` : '1',
					insertTime: Date.now(),
					isSynced: false,
					scheduledLegId: action.payload.scheduledLegId,
					type: action.payload.state,
					ref: action.payload.ref,
					checkDone: action.payload.checkDone,
					vehicleData: (action.payload as unknown as any).vehicleData, //this is the placce where we store replies for vehicle checks in serialized form
				});
				//await dependencies.db.scheduledLeg.upsert({ ...action.payload });
			})
		)
	);

/** Not used */
export const setBookingsEffect: Effect<SetBookingsAction> = (
	actions$,
	dependencies
) =>
	actions$.pipe(
		ofType(ScheduleActionTypes.SetBookings),
		concatMap((action: SetBookingsAction) =>
			defer(async () => {
				await dependencies.db['booking'].bulkUpsert(
					Object.values(action.payload)
				);
			})
		)
	);

export const editBookingEffect: Effect<EditBookingAction> = (
	actions$,
	dependencies
) =>
	actions$.pipe(
		ofType(ScheduleActionTypes.EditBooking),
		concatMap((action: EditBookingAction) =>
			defer(async () => {
				const lastChange = await dependencies.db['dataChanges']
					.findOne({ selector: {}, sort: [{ changeId: 'desc' }] })
					.exec();
				const lastChangeObj = lastChange?.toJSON();

				await dependencies.db['dataChanges'].upsert({
					...action.payload.dataChange,
					changeId: lastChangeObj ? `${+lastChangeObj.changeId + 1}` : '1',
					insertTime: Date.now(),
				});
			})
		)
	);

/** Fetch the schedule and its bookings, store them and record the changes. */
const syncSchedule = async (dependencies: Dependencies) => {
	const newLegs = (await dependencies.api.get(
		'/api/drivers/schedule'
	)) as TDriversScheduleResponse;
	const normalizedScheduledLegs = prepareNormalizedScheduledLegs(newLegs);

	const legsIds = normalizedScheduledLegs.reduce<string[]>((p, c) => {
		if ((c.enteringBookings || c.exitingBookings) && c.scheduledLegId) {
			p.push(c.scheduledLegId);
		}
		return p;
	}, []);

	const newBookings: Booking[] = legsIds.length
		? await dependencies.api.post('/api/drivers/bookingLegs', legsIds)
		: [];

	const currentLegs: RxDocument<NormalizedScheduledLeg>[] =
		await dependencies.db['scheduledLeg'].find().exec();
	const prevLegs = currentLegs.map((l) => l.toMutableJSON());

	const nextIds = new Set(normalizedScheduledLegs.map((l) => l.scheduledLegId));
	const legsToRemove = prevLegs
		.filter((l) => !nextIds.has(l.scheduledLegId))
		.map((l) => l.scheduledLegId as string);

	/** The first load has nothing to compare against. */
	if (prevLegs.length) {
		const unseenDiff: RxDocument<ScheduleDiffEl>[] = await dependencies.db[
			'scheduleDiff'
		]
			.find()
			.exec();
		const { upsert, remove } = buildScheduleDiff(
			prevLegs,
			normalizedScheduledLegs,
			unseenDiff.map((d) => d.toMutableJSON())
		);

		if (upsert.length > 0) {
			await safeBulkUpsert(dependencies.db['scheduleDiff'], upsert);
		}
		if (remove.length > 0) {
			await dependencies.db['scheduleDiff'].bulkRemove(remove);
		}
	}

	await safeBulkUpsert(
		dependencies.db['scheduledLeg'],
		normalizedScheduledLegs
	);

	if (legsToRemove.length > 0) {
		await dependencies.db['scheduledLeg'].bulkRemove(legsToRemove);
	}

	const oldBookings = await (
		dependencies.db['booking'] as RxCollection<Booking>
	)
		.find()
		.exec();
	const bookingsToDelete: string[] = [];
	const bookingsToUpdate: Booking[] = [];
	oldBookings.forEach((booking) => {
		const matchingNewBooking = newBookings.find(
			(doc) => doc.bookingId === booking.bookingId
		);
		if (!matchingNewBooking) {
			// Booking no longer exists
			bookingsToDelete.push(booking.bookingId);
		} else if (!areBookingsEqual(booking, matchingNewBooking)) {
			// Booking exists but has changed
			bookingsToUpdate.push(matchingNewBooking);
		}
	});

	// Check for new bookings
	const existingBookingIds = new Set(
		oldBookings.map((booking) => booking.bookingId)
	);
	const newBookingsToAdd = newBookings.filter(
		(newBooking) =>
			!existingBookingIds.has(newBooking.bookingId) ||
			bookingsToUpdate.some(
				(updatedBooking) => updatedBooking.bookingId === newBooking.bookingId
			)
	);

	await safeBulkUpsert(
		dependencies.db['booking'] as RxCollection<Booking>,
		newBookingsToAdd
	);

	if (bookingsToDelete.length > 0) {
		await dependencies.db['booking'].bulkRemove(bookingsToDelete);
	}

	await cleanupDataChanges(dependencies);
};

/** The poll and the push share one queue, so each sync compares against the previous one. */
export const syncScheduleEffect: Effect<
	LoadScheduleAction | UpdateScheduleAction
> = (actions$, dependencies) =>
	actions$.pipe(
		ofType(
			ScheduleActionTypes.LoadSchedule,
			ScheduleActionTypes.UpdateSchedule
		),
		concatMap((action) =>
			defer(async () => {
				try {
					await syncSchedule(dependencies);
				} catch (err) {
					console.log(err);
					dependencies.Sentry.captureException(err);
				}

				/** Only a push shows the changes; a failed fetch still shows what the poll recorded. */
				if (action.type === ScheduleActionTypes.UpdateSchedule) {
					useScheduleChangesStore.getState().setShowRequested(true);
				}
			})
		)
	);

export const resetScheduleDiffEffect: Effect<ResetScheduleAction> = (
	actions$,
	dependencies
) =>
	actions$.pipe(
		ofType(ScheduleActionTypes.ResetScheduleDiff),
		concatMap((action) =>
			defer(async () => {
				try {
					useScheduleChangesStore.getState().reset();
					LocalNotifications.removeAllDeliveredNotifications();
					await dependencies.db['scheduleDiff'].find().remove();
				} catch (err) {
					console.log(err);
					dependencies.Sentry.captureException(err);
				}
			})
		)
	);

interface Payload {
	type: PushNotificationTypeEnum;
}

const scheduleUpdatePushTypes: PushNotificationTypeEnum[] = [
	PushNotificationTypeEnum.PLAN_CHANGE,
	PushNotificationTypeEnum.PLAN_DELAY,
	PushNotificationTypeEnum.BOOKING_CANCELED_FOR_DRIVER,
];

export const initPushProcessorEffect: Effect<InitPushProcessorAction> = (
	actions$,
	dependencies
) =>
	actions$.pipe(
		ofType(ScheduleActionTypes.InitPushProcessorAction),
		concatMap((action: InitPushProcessorAction) =>
			defer(async () => {
				// TODO this should be more sophisticated
				if (pushInitialized) {
					return;
				}
				pushInitialized = true;
				const logger = Logger.getInstance('initPushProcessorEffect');

				await FirebaseMessaging.addListener('notificationReceived', (event) => {
					logger.debug('notificationReceived', { event });
					const payload = event.notification.data as Payload;
					if (scheduleUpdatePushTypes.includes(payload.type)) {
						actions$.next(updateSchedule() as Action);
					}
				});

				await FirebaseMessaging.addListener('tokenReceived', async (event) => {
					// send the token to the mopla backend
					await dependencies.api.post('/api/command/createUserPushToken', {
						pushToken: event.token,
						appContext: 'DRIVER',
					});
				});

				await FirebaseMessaging.addListener(
					'notificationActionPerformed',
					(event) => {
						// Note: We need notificationActionPerformed to update the schedule when the user taps on the notification
						logger.debug('notificationActionPerformed', { event });
						const payload = event.notification.data as Payload;
						if (scheduleUpdatePushTypes.includes(payload.type)) {
							actions$.next(updateSchedule() as Action);
						}
					}
				);
			})
		)
	);

export const initEventStreamEffect: Effect<InitEventStreamAction> = (
	actions$,
	dependencies
) =>
	actions$.pipe(
		ofType(ScheduleActionTypes.InitEventStream),
		first(),
		concatMap(() =>
			defer(() => {
				const dataChangesQuery = (
					dependencies.db['dataChanges'] as RxCollection<DataChange>
				).find({
					selector: {
						isSynced: {
							$eq: false,
						},
					},
				}).$;

				const networkStatusPipe$ = networkStatus$.pipe(distinctUntilChanged());

				return combineLatest([
					networkStatusPipe$,
					dataChangesQuery,
					timer(0, 1000 * 60 * 2),
				]).pipe(
					concatMap(
						([, changes]: [boolean, RxDocument<DataChange>[], number]) =>
							defer(async () => {
								/** Find the first non-synced document in the list */
								const currentChange = changes.find(
									(change) => !change.isSynced
								);

								try {
									if (!currentChange) {
										actions$.next(loadScheduleAction() as Action);
										return;
									}

									const changeType = currentChange.type;
									const isCancelledBooking =
										isBookingStatusCancelled(changeType);

									if (currentChange.ref === 'VEHICLE_CHECK') {
										if (currentChange.checkDone === true) {
											await dependencies.api.post('/api/vehicleCheck', {
												assignmentId: currentChange.scheduledLegId,
												...JSON.parse(currentChange.vehicleData || ''),
											});
										}
										await currentChange.incrementalPatch({ isSynced: true });
									} else if (
										currentChange.scheduledLegId &&
										!currentChange.bookingId &&
										!currentChange.vehicleData
									) {
										//this is either a scheduled leg or a scheduled break
										if (changeType === 'EXECUTING') {
											await dependencies.api.post('/api/command/startDrive', {
												scheduledLegId: currentChange.scheduledLegId,
											});
										}

										if (changeType === 'DONE') {
											await dependencies.api.post('/api/command/endDrive', {
												scheduledLegId: currentChange.scheduledLegId,
											});
										}

										await currentChange.incrementalPatch({ isSynced: true });
									} else if (
										currentChange.bookingId &&
										currentChange.scheduledLegId &&
										!currentChange.vehicleData
									) {
										await handleCheckIn({
											actions$,
											currentChange,
											dependencies,
											isCancelledBooking,
										});
									}
								} catch (err: any) {
									const requestError = 'cause' in err ? err.cause : err;

									if (
										axios.isAxiosError(requestError) &&
										requestError.response &&
										// in case of network error, we can just wait for the next timer and retry
										requestError.message !== 'Network Error'
									) {
										if (
											currentChange &&
											requestError.response.status &&
											[400, 422, 404].includes(requestError.response.status)
										) {
											// drop doc, because it has conflicts we cannot resolve
											dependencies.Sentry.addBreadcrumb({
												message: 'dropped local change due to server conflict',
												data: currentChange.toJSON(),
											});
											await safeRemoveDocument(currentChange);
										} else if (currentChange) {
											const attempt = currentChange.attempts
												? currentChange.attempts + 1
												: 1;
											if (attempt <= 5) {
												await currentChange.incrementalPatch({
													attempts: attempt,
												});
											} else {
												dependencies.Sentry.addBreadcrumb({
													message:
														'dropped local change after 5 unsuccessfull retries',
													data: currentChange.toJSON(),
												});

												await safeRemoveDocument(currentChange);

												/** This will be called when an API call error occurs with a status other than 400, 422, 404, after 5 consecutive attempts */
												if (err instanceof CheckInError) {
													actions$.next(failCheckInAction() as Action);
												}
											}
										}
									}

									dependencies.Sentry.captureException(err);
								}
							})
					)
				);
			})
		)
	);

const cleanupDataChanges = async (dependencies: Dependencies) => {
	const dataChangesCol: RxCollection<DataChange> =
		dependencies.db['dataChanges'];

	const docsToRemove = await dataChangesCol
		.find({
			selector: {
				$or: [{ isSynced: { $eq: true } }, { type: { $eq: 'PRE_FINISHED' } }],
			},
		})
		.exec();

	if (!docsToRemove.length) {
		return;
	}

	try {
		const docsIdsToRemove = docsToRemove.map((doc) => doc.changeId);
		await dataChangesCol.bulkRemove(docsIdsToRemove);
	} catch (e: any) {
		/**
		 * P2 error might occur when no docs were deleted.
		 * This might happen in case those docs were deleted somewhere in parallel (race conditions), which is fine.
		 * */
		if (e.code !== 'P2') {
			Sentry.captureException(e);
		}
	}
};

interface IHandleCheckInParams {
	currentChange: RxDocument<DataChange>;
	actions$: Subject<InitEventStreamAction>;
	dependencies: Dependencies;
	isCancelledBooking: boolean;
}

const handleCheckIn = async ({
	currentChange,
	dependencies,
	actions$,
	isCancelledBooking,
}: IHandleCheckInParams) => {
	actions$.next(startCheckInAction() as Action);

	try {
		if (currentChange.type === 'EXECUTING') {
			await dependencies.api.post<CheckInPassengerView>(
				'/api/drivers/checkInPassenger',
				{
					bookingId: currentChange.bookingId,
					scheduledLegId: currentChange.scheduledLegId,
				}
			);
		} else if (isCancelledBooking && !currentChange.isSynced) {
			const { bookingId, scheduledLegId, options } = currentChange;

			const { schedulerId } = await dependencies.api.post<CheckInPassengerView>(
				'/api/drivers/checkInPassenger',
				{
					bookingId,
					scheduledLegId,
					cancellationReason: options?.cancellationReason,
					cancellationDescription: options?.cancellationDescription,
				}
			);

			if (schedulerId) {
				await poll({
					callback: () =>
						dependencies.api.get<SchedulerView>(
							`/api/run-scheduler/${schedulerId}`
						),
					until: (result) =>
						result.schedulerState === SchedulerStateEnum.IN_PROGRESS,
				});
			}
		}

		await currentChange.incrementalPatch({ isSynced: true });
	} catch (error: any) {
		/** It is important to throw the error outside to handle it in the initEventStreamEffect effect's catch block */
		throw new CheckInError(error.message, error);
	} finally {
		actions$.next(stopCheckInAction() as Action);
	}
};

export class CheckInError extends Error {
	public cause: unknown;

	constructor(message?: string, cause?: Error) {
		super(message);
		this.name = 'CheckInError';
		this.cause = cause;
	}
}
